/**
 * Copyright (c) 2024 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Modal, BaseModalProps } from '@blend/shared-react';
import { colors, mixins } from '@blend/consumer-components';
import { css, StyleSheet } from 'aphrodite';
import { getFormValues, InjectedFormProps, reduxForm, SubmissionError } from 'redux-form';

import { connect } from 'react-redux';
import { TaskForm } from '../../form/TaskForm';
import { InputConfig } from '../../form/TaskInputs/types';

import { State } from '../../../redux';
import { TaskInputs } from '../../../components/form/TaskInputs/TaskInputs';
import { getFilteredInputs } from '../../../utils/formUtils';
import { participantAPI } from '../../../api/participant';
import { generalErrorMessage, issuesEncouteredInputConfigs } from '../constants';
import { defaultTo, noop } from '@utils/lodash';
import { Wrapper, Error } from '@components/form/Inputs/InputError';
import { DefaultSubmitInput } from '@components/form/DefaultSubmitInput/DefaultSubmitInput';

const FORM_NAME = 'participant_feedback_modal';

const overlayStyleConfig = {
  backgroundColor: colors.catalyst,
};

const styles = StyleSheet.create({
  contentWrapper: {
    ...mixins.margin(40, 75, 40, 75),
  },
  headerText: {
    fontSize: 24,
    textAlign: 'center',
    fontWeight: 'bold',
    marginBottom: 40,
  },
  helperText: {
    fontSize: 16,
    lineHeight: '22px',
    marginBottom: 24,
  },
  buttonWrapper: {
    marginTop: 30,
  },
  submitDescription: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    color: colors.catalystLight80,
    ...mixins.margin(30, 0, 0, 0),
  },
  feedbackWrapper: {
    ...mixins.margin(16, 0, 0, 0),
  },
});

export namespace FeedbackModal {
  export interface OwnProps extends Omit<BaseModalProps, 'handleClose'> {
    callId?: string;
    satisfactionScore: number;
    onSubmitSuccess: () => void;
    isSubmitting: boolean;
    setIsSubmitting: Dispatch<SetStateAction<boolean>>;
  }
  export interface StateProps {
    formValues: Record<string, any>;
  }

  export type Props = InjectedFormProps<{}, StateProps & OwnProps> & OwnProps & StateProps;
}

const _FeedbackModal: React.FC<FeedbackModal.Props> = ({ handleSubmit, error, formValues, ...props }) => {
  const [inputs, setInputs] = useState<InputConfig[]>([]);

  const formTemplate = (
    <>
      <TaskInputs inputs={inputs} data-it="feedbackInputs" />
      <div className={css(styles.buttonWrapper)}>
        <DefaultSubmitInput disabled={props.isSubmitting}>Submit</DefaultSubmitInput>
      </div>
    </>
  );

  useEffect(() => {
    const filteredInputs = getFilteredInputs(issuesEncouteredInputConfigs, formValues);
    setInputs(filteredInputs);
  }, [formValues]);

  return (
    <Modal overlayStyleConfig={overlayStyleConfig} data-it="FeedbackModal" handleClose={noop} isOpen={props.isOpen}>
      <div className={css(styles.contentWrapper)}>
        <div className={css(styles.headerText)}>We would appreciate your feedback</div>
        <TaskForm onSubmit={handleSubmit} formTemplate={formTemplate} />
        {error && (
          <Wrapper id={'general-error'}>
            <Error error={error} />
          </Wrapper>
        )}
      </div>
    </Modal>
  );
};

const mapStateToProps = (state: State) => {
  return {
    formValues: getFormValues(FORM_NAME)(state),
  };
};

const onSubmit = async (
  formValues: any,
  _dispatch: any,
  {
    callId,
    satisfactionScore,
    onSubmitSuccess,
    setIsSubmitting,
  }: {
    callId?: string;
    satisfactionScore: number;
    onSubmitSuccess: () => void;
    setIsSubmitting: Dispatch<SetStateAction<boolean>>;
  },
) => {
  setIsSubmitting(true);
  const { issuesEncountered, additionalFeedback, notaryRating } = formValues;
  try {
    const requestbody = {
      satisfactionScore,
      issues: defaultTo(issuesEncountered, []),
      userFeedback: additionalFeedback,
      notaryRating,
    };
    await participantAPI.calls.createParticipantFeedback(callId!, requestbody);
    onSubmitSuccess();
  } catch (e) {
    setIsSubmitting(false);
    throw new SubmissionError({ _error: generalErrorMessage });
  }
};

export const FeedbackModal = connect(mapStateToProps)(
  reduxForm<{}, FeedbackModal.OwnProps & FeedbackModal.StateProps>({
    form: FORM_NAME,
    enableReinitialize: true,
    keepDirtyOnReinitialize: true,
    onSubmit,
  })(_FeedbackModal),
);
